import { RevShareEntry } from '../../../models/RevShareData';
import { formatDateMDYY } from '../../../helpers/utilityFunctions';

type RevShareEntryStatus = 'Active' | 'Scheduled' | 'Expired';

interface OverlapValidationResult {
  valid: boolean;
  errorMessage?: string;
}

// API responses can return null for fields typed as non-null; normalize to safe defaults.
export const normalizeRevShareEntry = (entry: RevShareEntry): RevShareEntry => ({
  ...entry,
  organizationId: entry.organizationId ?? 0,
  teamId: entry.teamId ?? 0,
  groupId: entry.groupId ?? 0,
  baseAmount: entry.baseAmount ?? 0,
  revSharePct: entry.revSharePct ?? 0,
  fixedAmount: entry.fixedAmount ?? 0,
  totalAmount: entry.totalAmount ?? 0,
  revShareDorp: entry.revShareDorp?.toUpperCase() === 'P' ? 'P' : 'D',
  expireTimestamp: entry.expireTimestamp ?? '',
  disabledRevShare: entry.disabledRevShare ?? false,
  tier: entry.tier ?? '',
});

const MS_PER_DAY = 86_400_000;

export const getEntryStatus = (entry: RevShareEntry, today: Date): RevShareEntryStatus => {
  const effectiveDate = new Date(entry.effectiveTimestamp);
  const startOfToday = Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate());
  const endOfToday = startOfToday + MS_PER_DAY - 1;

  if (effectiveDate.getTime() > endOfToday) {
    return 'Scheduled';
  }

  const expiration = entry.expireTimestamp;
  if (expiration) {
    const expirationDate = new Date(expiration);
    if (expirationDate.getTime() < startOfToday) {
      return 'Expired';
    }
  }

  return 'Active';
};

export const formatEffectiveRange = (entry: RevShareEntry): string => {
  const effectiveDate = formatDateMDYY(entry.effectiveTimestamp);
  const expirationDate = entry.expireTimestamp ? formatDateMDYY(entry.expireTimestamp) : 'ongoing';
  return `${effectiveDate} – ${expirationDate}`;
};

export const validateDateOverlap = (
  newEffective: Date,
  newExpiration: Date | null,
  existingEntries: RevShareEntry[],
  excludeEntryId?: number,
): OverlapValidationResult => {
  const otherEntries = existingEntries.filter(e => e.id !== excludeEntryId);
  if (otherEntries.length === 0) return { valid: true };

  const chronological = [...otherEntries].sort(
    (a, b) => new Date(a.effectiveTimestamp).getTime() - new Date(b.effectiveTimestamp).getTime(),
  );

  const priorEntry = findPriorEntry(chronological, newEffective);
  const followingEntry = findFollowingEntry(chronological, newEffective);

  if (followingEntry && !newExpiration) {
    return {
      valid: false,
      errorMessage: `You must set an expiration date before ${formatDateMDYY(followingEntry.effectiveTimestamp)} to prevent overlap.`,
    };
  }

  if (priorEntry && effectiveOverlapsPriorEntry(newEffective, priorEntry)) {
    if (
      followingEntry &&
      newExpiration &&
      expirationOverlapsFollowingEntry(newExpiration, followingEntry)
    ) {
      return validateBothSidesOverlap(priorEntry, followingEntry);
    }
    return validatePriorOverlap(newEffective, priorEntry);
  }

  if (
    followingEntry &&
    newExpiration &&
    expirationOverlapsFollowingEntry(newExpiration, followingEntry)
  ) {
    return {
      valid: false,
      errorMessage: `Choose an expiration date before ${formatDateMDYY(followingEntry.effectiveTimestamp)} to prevent overlap.`,
    };
  }

  return { valid: true };
};

export const hasSubsequentEntry = (
  entries: RevShareEntry[],
  effectiveDate: Date,
  excludeEntryId?: number,
): boolean => {
  return entries.some(
    e => e.id !== excludeEntryId && new Date(e.effectiveTimestamp) > effectiveDate,
  );
};

const validateBothSidesOverlap = (
  priorEntry: RevShareEntry,
  followingEntry: RevShareEntry,
): OverlapValidationResult => {
  const priorReference = priorEntry.expireTimestamp || priorEntry.effectiveTimestamp;
  return {
    valid: false,
    errorMessage: `Choose an effective date after ${formatDateMDYY(priorReference)} and an expiration date before ${formatDateMDYY(followingEntry.effectiveTimestamp)} to prevent overlap.`,
  };
};

const toCalendarDay = (dateOrTimestamp: Date | string): number => {
  const d = typeof dateOrTimestamp === 'string' ? new Date(dateOrTimestamp) : dateOrTimestamp;
  return Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate());
};

// Open-ended prior entries get auto-adjusted by the backend on save, so only
// block same-day overlap (the one case auto-adjust can't handle).
const validatePriorOverlap = (
  newEffective: Date,
  priorEntry: RevShareEntry,
): OverlapValidationResult => {
  if (!priorEntry.expireTimestamp) {
    if (toCalendarDay(priorEntry.effectiveTimestamp) === toCalendarDay(newEffective)) {
      return {
        valid: false,
        errorMessage: `Choose an effective date after ${formatDateMDYY(priorEntry.effectiveTimestamp)} to prevent overlap.`,
      };
    }
    return { valid: true };
  }

  return {
    valid: false,
    errorMessage: `Choose an effective date after ${formatDateMDYY(priorEntry.expireTimestamp)} to prevent overlap.`,
  };
};

const findPriorEntry = (
  chronological: RevShareEntry[],
  effectiveDate: Date,
): RevShareEntry | undefined => {
  const effectiveDay = toCalendarDay(effectiveDate);
  const candidates = chronological.filter(e => toCalendarDay(e.effectiveTimestamp) <= effectiveDay);
  return candidates[candidates.length - 1];
};

const findFollowingEntry = (
  chronological: RevShareEntry[],
  effectiveDate: Date,
): RevShareEntry | undefined => {
  const effectiveDay = toCalendarDay(effectiveDate);
  return chronological.find(e => toCalendarDay(e.effectiveTimestamp) > effectiveDay);
};

const effectiveOverlapsPriorEntry = (effectiveDate: Date, priorEntry: RevShareEntry): boolean => {
  if (!priorEntry.expireTimestamp) return true;
  return toCalendarDay(effectiveDate) <= toCalendarDay(priorEntry.expireTimestamp);
};

const expirationOverlapsFollowingEntry = (
  expirationDate: Date,
  followingEntry: RevShareEntry,
): boolean => {
  return toCalendarDay(expirationDate) >= toCalendarDay(followingEntry.effectiveTimestamp);
};
